/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

// Mobile touch support for product item secondary images
document.addEventListener("DOMContentLoaded", function () {
  // Find all product items with secondary images
  const productItems = document.querySelectorAll(
    ".product-item__image-wrapper--multiple"
  );

  // Preload secondary images after primary images are loaded
  preloadSecondaryImages(productItems);

  productItems.forEach(function (productItem) {
    let touchStartTime = 0;
    let touchEndTime = 0;
    let isTouchActive = false;
    const touchDuration = 500; // Duration in milliseconds to show secondary image

    // Handle touch start
    productItem.addEventListener(
      "touchstart",
      function (event) {
        touchStartTime = new Date().getTime();
        isTouchActive = true;

        // Don't prevent default to allow slider dragging
        // event.preventDefault();

        // Show secondary image immediately on touch start
        const primaryImage = productItem.querySelector(
          ".product-item__primary-image"
        );
        const secondaryImage = productItem.querySelector(
          ".product-item__secondary-image"
        );

        if (primaryImage && secondaryImage) {
          primaryImage.style.visibility = "hidden";
          primaryImage.style.opacity = "0";
          secondaryImage.style.visibility = "visible";
          secondaryImage.style.opacity = "1";
          secondaryImage.style.display = "block";
        }
      },
      { passive: true }
    );

    // Handle touch end
    productItem.addEventListener(
      "touchend",
      function (event) {
        if (!isTouchActive) return;

        touchEndTime = new Date().getTime();
        const touchTime = touchEndTime - touchStartTime;
        isTouchActive = false;

        // Revert to primary image after a delay
        setTimeout(
          function () {
            if (!isTouchActive) {
              const primaryImage = productItem.querySelector(
                ".product-item__primary-image"
              );
              const secondaryImage = productItem.querySelector(
                ".product-item__secondary-image"
              );

              if (primaryImage && secondaryImage) {
                primaryImage.style.visibility = "visible";
                primaryImage.style.opacity = "1";
                secondaryImage.style.visibility = "hidden";
                secondaryImage.style.opacity = "0";
                secondaryImage.style.display = "none";
              }
            }
          },
          touchTime < touchDuration ? 800 : 0
        );
      },
      { passive: true }
    );

    // Handle touch cancel (when user scrolls or moves finger away)
    productItem.addEventListener(
      "touchcancel",
      function (event) {
        isTouchActive = false;

        const primaryImage = productItem.querySelector(
          ".product-item__primary-image"
        );
        const secondaryImage = productItem.querySelector(
          ".product-item__secondary-image"
        );

        if (primaryImage && secondaryImage) {
          primaryImage.style.visibility = "visible";
          primaryImage.style.opacity = "1";
          secondaryImage.style.visibility = "hidden";
          secondaryImage.style.opacity = "0";
          secondaryImage.style.display = "none";
        }
      },
      { passive: true }
    );
  });

  /* Rich text show more/less functionality is now handled by pure CSS */

  /* Image with text show more/less functionality is now handled by pure CSS */
});

// Preload secondary images function
function preloadSecondaryImages(productItems) {
  if (productItems.length === 0) return;

  // Get all primary images first
  const primaryImages = [];
  productItems.forEach(function (productItem) {
    const primaryImage = productItem.querySelector(
      ".product-item__primary-image"
    );
    if (primaryImage) {
      primaryImages.push(primaryImage);
    }
  });

  let loadedPrimaryImages = 0;
  const totalPrimaryImages = primaryImages.length;

  // Check if primary images are already loaded
  primaryImages.forEach(function (img) {
    if (img.complete) {
      loadedPrimaryImages++;
    } else {
      img.addEventListener("load", function () {
        loadedPrimaryImages++;
        if (loadedPrimaryImages === totalPrimaryImages) {
          preloadSecondaryImagesPhase2(productItems);
        }
      });
    }
  });

  // If all primary images are already loaded, proceed to phase 2
  if (loadedPrimaryImages === totalPrimaryImages) {
    preloadSecondaryImagesPhase2(productItems);
  }
}

function preloadSecondaryImagesPhase2(productItems) {
  // Now preload all secondary images
  productItems.forEach(function (productItem) {
    const secondaryImage = productItem.querySelector(
      ".product-item__secondary-image"
    );
    if (secondaryImage && secondaryImage.dataset.src) {
      // Create a new image element to preload
      const preloadImg = new Image();
      preloadImg.src = secondaryImage.dataset.src;
      preloadImg.onload = function () {
        // Once loaded, update the actual secondary image
        secondaryImage.src = secondaryImage.dataset.src;
        secondaryImage.removeAttribute("data-src");
      };
    }
  });
}

// USPs Modal Functionality
function initializeUSPsModals() {
  console.log("USPs Modal: Initializing modals");

  // Debug: Check what elements exist on the page
  console.log(
    "USPs Modal: All elements with 'info' in class:",
    document.querySelectorAll("[class*='info']")
  );
  console.log(
    "USPs Modal: All elements with 'modal' in class:",
    document.querySelectorAll("[class*='modal']")
  );
  console.log(
    "USPs Modal: All elements with 'close' in class:",
    document.querySelectorAll("[class*='close']")
  );

  // Handle all info icons on the page
  var icons = document.querySelectorAll(".info-icon");
  console.log("USPs Modal: Found", icons.length, "info icons");

  var closes = document.querySelectorAll(".close");
  console.log("USPs Modal: Found", closes.length, "close buttons");

  icons.forEach(function (icon, index) {
    // Check if this icon already has a click listener
    if (icon.hasAttribute("data-modal-initialized")) {
      return; // Skip if already initialized
    }

    console.log("USPs Modal: Adding click listener to icon", index, icon);
    icon.setAttribute("data-modal-initialized", "true");

    icon.addEventListener("click", function () {
      var modalId = this.getAttribute("data-modal");
      console.log("USPs Modal: Icon clicked, modalId:", modalId);
      var modal = document.getElementById(modalId);
      console.log("USPs Modal: Found modal:", modal);
      if (modal) {
        modal.style.display = "block";
        console.log("USPs Modal: Modal displayed");
      } else {
        console.log("USPs Modal: Modal not found with ID:", modalId);
      }
    });
  });

  closes.forEach(function (close, index) {
    // Check if this close button already has a click listener
    if (close.hasAttribute("data-modal-initialized")) {
      return; // Skip if already initialized
    }

    console.log(
      "USPs Modal: Adding click listener to close button",
      index,
      close
    );
    close.setAttribute("data-modal-initialized", "true");

    close.addEventListener("click", function () {
      var modalWrapper = this.closest(".modal-wrapper");
      console.log("USPs Modal: Close clicked, modalWrapper:", modalWrapper);
      if (modalWrapper) {
        modalWrapper.style.display = "none";
        console.log("USPs Modal: Modal hidden");
      }
    });
  });
}

// Initialize on DOMContentLoaded
document.addEventListener("DOMContentLoaded", function () {
  console.log("USPs Modal: DOMContentLoaded fired");
  initializeUSPsModals();
});

// Use MutationObserver instead of deprecated DOMNodeInserted
var observer = new MutationObserver(function (mutations) {
  var shouldInitialize = false;
  mutations.forEach(function (mutation) {
    if (mutation.type === "childList") {
      mutation.addedNodes.forEach(function (node) {
        if (node.nodeType === 1) {
          // Element node
          if (
            node.classList &&
            (node.classList.contains("info-icon") ||
              node.classList.contains("close"))
          ) {
            shouldInitialize = true;
          }
          // Also check child elements
          var childIcons =
            node.querySelectorAll &&
            node.querySelectorAll(".info-icon, .close");
          if (childIcons && childIcons.length > 0) {
            shouldInitialize = true;
          }
        }
      });
    }
  });

  if (shouldInitialize) {
    console.log("USPs Modal: New modal elements detected via MutationObserver");
    setTimeout(initializeUSPsModals, 100);
  }
});

// Start observing when DOM is ready
document.addEventListener("DOMContentLoaded", function () {
  observer.observe(document.body, {
    childList: true,
    subtree: true,
  });
});

// Close modal when clicking outside
window.addEventListener("click", function (event) {
  var modalWrappers = document.querySelectorAll(".modal-wrapper");
  modalWrappers.forEach(function (modal) {
    if (event.target === modal) {
      console.log("USPs Modal: Clicked outside modal, hiding");
      modal.style.display = "none";
    }
  });
});

// Samples add-to-cart behavior
(function () {
  function handleSelectionState(root) {
    const allCheckboxes = root.querySelectorAll(
      ".sample-card__checkbox, .color-swatch__checkbox"
    );
    const checked = Array.from(allCheckboxes).filter((c) => c.checked);
    const limit = 4;

    if (checked.length >= limit) {
      allCheckboxes.forEach((cb) => {
        if (!cb.checked) {
          const wrapper = cb.closest(
            ".sample-card__label, .color-swatch, .sample-card"
          );
          if (wrapper) wrapper.classList.add("sample--disabled");
        }
      });
    } else {
      allCheckboxes.forEach((cb) => {
        const wrapper = cb.closest(
          ".sample-card__label, .color-swatch, .sample-card"
        );
        if (wrapper) wrapper.classList.remove("sample--disabled");
      });
    }
  }

  function addSamplesInit() {
    document.querySelectorAll("#AddSamplesToCart").forEach(function (button) {
      if (button.dataset.samplesBound === "1") return;
      button.dataset.samplesBound = "1";

      // Enforce max selection logic inside the same container
      const root =
        button.closest(".drawer__content, .popover__content") || document;
      // In-root change handler: enforce limit + show notification
      root.addEventListener(
        "change",
        function (e) {
          const target = e.target;
          if (
            target &&
            (target.matches(".sample-card__checkbox") ||
              target.matches(".color-swatch__checkbox"))
          ) {
            const all = root.querySelectorAll(
              ".sample-card__checkbox:checked, .color-swatch__checkbox:checked"
            );
            if (all.length > 4) {
              // Revert this selection if over limit
              target.checked = false;
            } else if (target.checked) {
              // Show slide notification with variant name
              const name =
                target.getAttribute("data-variant-name") ||
                (target.closest(".color-swatch") &&
                  target
                    .closest(".color-swatch")
                    .getAttribute("data-variant-name")) ||
                target.id ||
                "";
              if (name) showSampleNotification(name, root);
            }
            handleSelectionState(root);
          }
        },
        true
      );

      // Attach in capture phase to preempt other listeners (e.g., cart drawer handlers on generic buttons)
      button.addEventListener(
        "click",
        async function (e) {
          try {
            // Prevent generic add-to-cart click handlers from opening the cart too early
            if (e) {
              e.preventDefault();
              e.stopPropagation();
              if (typeof e.stopImmediatePropagation === "function")
                e.stopImmediatePropagation();
            }

            const root =
              button.closest(".drawer__content, .popover__content") || document;
            // Support both legacy swatch checkboxes and new image-card checkboxes
            const checkboxes = root.querySelectorAll(
              ".sample-card__checkbox:checked, .color-swatch__checkbox:checked"
            );
            const ids = Array.from(checkboxes)
              .map(function (cb) {
                return cb.value;
              })
              .filter(Boolean)
              .slice(0, 4); // hard cap

            if (ids.length === 0) {
              button.classList.add("shake");
              setTimeout(function () {
                button.classList.remove("shake");
              }, 600);
              return;
            }

            // Show loader state
            button.setAttribute("aria-busy", "true");
            button.disabled = true;

            // Batch into a single POST with multiple items when possible
            const items = ids.map(function (id) {
              return { id: Number(id), quantity: 1 };
            });
            let success = true;
            const res = await fetch("/cart/add.js", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              body: JSON.stringify({ items: items }),
            });

            if (!res.ok) {
              // Fallback: try sequential add
              for (const id of ids) {
                const r = await fetch("/cart/add.js", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                    Accept: "application/json",
                  },
                  body: JSON.stringify({ id: Number(id), quantity: 1 }),
                });
                if (!r.ok) {
                  success = false;
                  break;
                }
              }
            }

            if (!success) throw new Error("Add failed");

            // Notify theme and Vue drawer to refresh and open
            document.documentElement.dispatchEvent(
              new CustomEvent("cart:refresh", { bubbles: true })
            );
            document.documentElement.dispatchEvent(
              new CustomEvent("cart:updated", { bubbles: true })
            );

            // Close popover/drawer after dispatching updates
            const container = button.closest("drawer-content, popover-content");
            if (container) container.open = false;
          } catch (e) {
            console.error("Failed to add samples:", e);
            alert(
              "Es gab ein Problem beim Hinzufügen der Muster. Bitte versuchen Sie es erneut."
            );
          } finally {
            button.removeAttribute("aria-busy");
            button.disabled = false;
          }
        },
        true
      );

      // Initialize selection state on open
      handleSelectionState(root);
    });
  }

  document.addEventListener("DOMContentLoaded", addSamplesInit);
  document.addEventListener("openable-element:open", addSamplesInit, true);
})();

// Non-overlapping sample selection notification (per container)
const __sampleNotifState = new WeakMap();
function showSampleNotification(text, root) {
  try {
    const drawer = root && root.closest ? root.closest(".drawer") : null;
    const popover = root && root.closest ? root.closest(".popover") : null;
    const container =
      drawer ||
      popover ||
      (root && root.querySelector && root.querySelector(".sample-notif")
        ? root
        : document);

    let state = __sampleNotifState.get(container);
    if (!state) {
      const el = container.querySelector(".sample-notif");
      if (!el) return; // no container element present
      const inner = el.querySelector(".sample-notif__text");
      const close = el.querySelector(".sample-notif__close");
      state = { el, inner, close, timer: null };
      __sampleNotifState.set(container, state);
      if (close) {
        close.addEventListener("click", function () {
          if (state.timer) {
            clearTimeout(state.timer);
            state.timer = null;
          }
          state.el.classList.remove("sample-notif--visible");
        });
      }
    }
    state.inner.textContent = text;
    state.el.classList.remove("sample-notif--visible");
    if (state.timer) {
      clearTimeout(state.timer);
      state.timer = null;
    }
    // Force reflow
    void state.el.offsetWidth;
    state.el.classList.add("sample-notif--visible");
    state.timer = setTimeout(function () {
      state.el.classList.remove("sample-notif--visible");
      state.timer = null;
    }, 3600);
  } catch (e) {
    console.warn("Sample notification failed:", e);
  }
}


/* COLOR CASCADING in den Empfehlungen — v7
 * Änderungen ggü. v6:
 * - Card-internen Swatch-Klick-Listener entgrenzt: funktioniert jetzt
 *   nicht nur in Empfehlungs-Sections, sondern auf jeder Seite mit
 *   <product-item> Cards (Startseite, Kategorie etc.).
 * - Neu: Touch-Event Hover-Emulation. iOS Safari triggert sein natives
 *   :hover-on-tap in der Empfehlungs-Section nicht (vermutlich wegen
 *   Flickity-Touch-Capture). Wir klassen-toggeln "is-touched" auf
 *   touchstart / touchend / touchcancel / touchmove. Funktioniert
 *   universell, ohne Konflikt mit nativem Desktop :hover.
 *
 * Begleitend nötig: ein kleines CSS-Snippet (siehe Doku-Kommentar
 * unten oder cascading_block_v7.css).
 */
(function () {
  'use strict';
  var CARD_SELECTOR = [
    '.shopify-section--product-recommendations product-item',
    '.shopify-section--cart-recommendations product-item',
    'product-recommendations product-item'
  ].join(', ');
  var EXCLUDE_FROM_DETECT =
    'product-recommendations, ' +
    '.shopify-section--product-recommendations, ' +
    '.shopify-section--cart-recommendations';

  var productCache = {};

  function cssEscapeAttr(value) {
    return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }
  function isLikelyColorValue(value) {
    if (!value) return false;
    return !!document.querySelector(
      '.color-swatch__radio[value="' + cssEscapeAttr(value) + '"]'
    );
  }
  function extractColorFromVariant(variant) {
    if (!variant) return null;
    var c = [variant.option1, variant.option2, variant.option3];
    for (var i = 0; i < c.length; i++)
      if (c[i] && isLikelyColorValue(c[i])) return c[i];
    return null;
  }

  function updateSecondaryImage(card, productJson, variantId) {
    var secondary = card.querySelector('.product-item__secondary-image');
    if (!secondary) return;
    var variant = (productJson.variants || []).find(function (v) {
      return String(v.id) === String(variantId);
    });
    if (!variant || !variant.featured_media) return;
    var fmPos = variant.featured_media.position;
    var nextMedia = (productJson.media || []).find(function (m) {
      return m.position === fmPos + 1;
    }) || variant.featured_media;
    if (!nextMedia || !nextMedia.src) return;
    secondary.src = nextMedia.src;
    secondary.srcset = nextMedia.src;
    secondary.alt = nextMedia.alt || '';
  }

  function applyToCard(card, radio) {
    if (!radio.checked) radio.checked = true;
    var variantId = radio.dataset.variantId;
    var fmId = radio.dataset.variantFeaturedMedia;

    if (variantId) {
      card.querySelectorAll('a[href*="/products/"]').forEach(function (a) {
        try {
          var url = new URL(a.getAttribute('href'), location.origin);
          url.searchParams.set('variant', variantId);
          a.setAttribute('href', url.pathname + url.search + url.hash);
        } catch (e) {}
      });
    }

    if (fmId) {
      var target = card.querySelector(
        'img[data-media-id="' + cssEscapeAttr(fmId) + '"]'
      );
      if (target) {
        card.querySelectorAll('img[data-media-id]').forEach(function (img) {
          if (img === target) img.removeAttribute('hidden');
          else img.setAttribute('hidden', '');
        });
      }
    }

    var link = card.querySelector('a[href*="/products/"]');
    if (!link || !variantId) return;
    var handle = link.pathname.replace(/^.*\/products\//, '').split('?')[0].split('/')[0];
    if (!handle) return;
    if (productCache[handle]) {
      updateSecondaryImage(card, productCache[handle], variantId);
    } else {
      fetch('/products/' + handle + '.js')
        .then(function (r) { return r.json(); })
        .then(function (p) {
          productCache[handle] = p;
          updateSecondaryImage(card, p, variantId);
        })
        .catch(function () {});
    }
  }

  function applyColorCascade(colorValue) {
    if (!colorValue) return;
    var escaped = cssEscapeAttr(colorValue);
    document.querySelectorAll(CARD_SELECTOR).forEach(function (card) {
      var radio = card.querySelector(
        '.color-swatch__radio[value="' + escaped + '"]'
      );
      if (radio) applyToCard(card, radio);
    });
  }

  function initialCascade() {
    var checked = document.querySelectorAll('input[type="radio"]:checked');
    for (var i = 0; i < checked.length; i++) {
      var r = checked[i];
      if (r.closest && r.closest(EXCLUDE_FROM_DETECT)) continue;
      if (isLikelyColorValue(r.value)) {
        applyColorCascade(r.value);
        return;
      }
    }
  }

  // Cascade vom Hauptprodukt
  document.addEventListener('variant:changed', function (event) {
  var src = event && event.target;
  if (src && src.closest && src.closest('quick-buy-drawer, quick-buy-popover')) {
    return;
  }
  var v = event && event.detail && event.detail.variant;
  var c = extractColorFromVariant(v);
  if (c) applyColorCascade(c);
});

  // Direkter Card-Swatch-Klick — auf jeder Seite (Recommendations,
  // Startseite, Collection-Listing usw.)
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.classList || !t.classList.contains('color-swatch__radio')) return;
    var card = t.closest && t.closest('product-item');
    if (!card) return;
    applyToCard(card, t);
  });

  // ---- Mobile Touch-Hover-Emulation ----
  // iOS Safari triggert sein natives :hover-on-tap nicht in Sections
  // mit Touch-Capture (z.B. Flickity). Wir setzen die Klasse selbst,
  // CSS aus custom.css macht das Secondary-Bild dann sichtbar.
  var touchedCard = null;
  function clearTouched() {
    if (touchedCard) { touchedCard.classList.remove('is-touched'); touchedCard = null; }
  }
  document.addEventListener('touchstart', function (e) {
    var a = e.target && e.target.closest && e.target.closest('a[href*="/products/"]');
    if (!a) return;
    var card = a.closest('product-item');
    if (!card) return;
    if (touchedCard && touchedCard !== card) clearTouched();
    touchedCard = card;
    card.classList.add('is-touched');
  }, { passive: true });
  document.addEventListener('touchend', clearTouched, { passive: true });
  document.addEventListener('touchcancel', clearTouched, { passive: true });

  function runInitial() { setTimeout(initialCascade, 800); }
  if (document.readyState === 'loading')
    document.addEventListener('DOMContentLoaded', runInitial);
  else runInitial();
})();






/* ============================================================
 * Reviews-Sterne-Scroll v1  (17.05.2026)
 * Theme-Kopie: "color cascading JS /hoffmann"
 *
 * Klick auf die Sterne-Anzeige (.ruk_rating_snippet) im
 * Produkt-Header scrollt smooth zum reviews.io-Widget weiter
 * unten auf der Produktseite.
 *
 * Einbau:  ans Ende von assets/custom.js anhängen
 * (unter dem v7-Color-Cascading-Block).
 * ============================================================ */
(function () {
  'use strict';

  // Reviews-Widget-Container — reviews.io rendert in .reviews-io-polaris-widget,
  // ID enthält dynamische Shopify-Section-Hash, deshalb über Klasse selektieren.
  function findReviewsTarget() {
    return (
      document.querySelector('.reviews-io-polaris-widget') ||
      document.querySelector('[id^="ReviewsWidget-"]') ||
      document.querySelector('.ruk-widget-container')
    );
  }

  function scrollToReviews(ev) {
    var target = findReviewsTarget();
    if (!target) return;
    if (ev) ev.preventDefault();

    // Header-Offset: Hoffmann-Header ist aktuell static, aber falls
    // später Sticky aktiviert wird, rechnen wir den sichtbaren Bottom
    // mit ein. Plus 16px Atemraum.
    var hdr = document.querySelector('store-header, .header');
    var headerOffset = 0;
    if (hdr) {
      var r = hdr.getBoundingClientRect();
      var cs = window.getComputedStyle(hdr);
      if ((cs.position === 'sticky' || cs.position === 'fixed') && r.bottom > 0) {
        headerOffset = r.bottom;
      }
    }

    var y = target.getBoundingClientRect().top + window.pageYOffset - headerOffset - 16;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  function enhanceBadge(badge) {
    if (!badge || badge.dataset.reviewsScrollBound === '1') return;
    badge.dataset.reviewsScrollBound = '1';
    badge.style.cursor = 'pointer';
    badge.setAttribute('role', 'button');
    badge.setAttribute('tabindex', '0');
    badge.setAttribute('aria-label', 'Zu den Bewertungen springen');
    badge.addEventListener('click', scrollToReviews);
    badge.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        scrollToReviews(e);
      }
    });
  }

  function bindAllBadges() {
    // Nur Badges im Produkt-Header binden — nicht in Recommendations-Cards o.ä.
    var selectors = [
      '.product-form .ruk_rating_snippet',
      '.product-form__custom-liquid .ruk_rating_snippet',
      '.product-meta .ruk_rating_snippet'
    ];
    document.querySelectorAll(selectors.join(',')).forEach(enhanceBadge);
  }

  // reviews.io rendert die Sterne asynchron — mehrfach nachjagen
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAllBadges);
  } else {
    bindAllBadges();
  }
  setTimeout(bindAllBadges, 500);
  setTimeout(bindAllBadges, 1500);
  setTimeout(bindAllBadges, 3500);

  // Falls reviews.io das Snippet erst nach Settle einfügt, MutationObserver
  // hängt die Listener nachträglich an.
  try {
    var obs = new MutationObserver(function () {
      // throttle: nur wenn neue unbound Badges auftauchen
      var anyUnbound = document.querySelector(
        '.product-form .ruk_rating_snippet:not([data-reviews-scroll-bound]),' +
        '.product-form__custom-liquid .ruk_rating_snippet:not([data-reviews-scroll-bound]),' +
        '.product-meta .ruk_rating_snippet:not([data-reviews-scroll-bound])'
      );
      if (anyUnbound) bindAllBadges();
    });
    obs.observe(document.body, { childList: true, subtree: true });
  } catch (e) {
    /* no-op */
  }
})();





/* ============================================================
 * Custom-Manufacture-Notice Toggle  v1  (17.05.2026)
 * Theme-Kopie: "color cascading JS /hoffmann"
 *
 * Toggelt das "Individuell für Sie gefertigt"-Feld
 * (#manufacture-notice) bei Variantenwechsel.
 *
 * Warum das hier ist:
 *  - Auf der Haupt-Produktseite macht das theme.js automatisch
 *    (irgendwo in den 300 KB — Regression vermutet, aber nicht
 *    kritisch zu fixen).
 *  - Im Quick-Buy-Drawer / -Popover funktioniert es NICHT:
 *      a) Mehrere #manufacture-notice können gleichzeitig im
 *         DOM stehen (Hauptseite + ein Drawer pro Empfehlungs-
 *         Karte). theme.js verwendet vermutlich
 *         getElementById('manufacture-notice'), trifft also nur
 *         das erste = die Hauptseite.
 *      b) Der Drawer-Inhalt wird lazy via fetch nachgeladen;
 *         beim DOMContentLoaded existiert er noch nicht, also
 *         hängt theme.js seinen Listener nie an.
 *
 * Was dieser Block tut:
 *  - Hört auf 'variant:changed' (Focal-Event aus jedem Form).
 *  - Bestimmt aus event.target den Scope (Drawer / Popover /
 *    Hauptseite) und togglet AUSSCHLIESSLICH die Notices in
 *    diesem Scope. Keine ID-Kollisionen mehr.
 *  - Liest den 'customized'-Status aus window.variantMetafields
 *    (gemerged, siehe Snippet-Patch 02_).
 *
 * Einbau:  ans Ende von assets/custom.js anhängen.
 * ============================================================ */
(function () {
  'use strict';

  // Liefert den DOM-Scope für ein variant:changed-Event:
  // - bevorzugt Drawer / Popover (Quick-Buy oder generisch),
  // - fallback auf das umgebende .product-form,
  // - notfalls document.
  // Wichtig: NICHT direkt auf document fallen, sonst toggelt
  // ein Variant-Wechsel auf der Hauptseite auch versehentlich
  // ein hängengebliebenes Drawer-#manufacture-notice.
  function scopeFor(formEl) {
    if (!formEl || !formEl.closest) return document;
    return (
      formEl.closest('quick-buy-drawer') ||
      formEl.closest('quick-buy-popover') ||
      formEl.closest('drawer-content') ||
      formEl.closest('popover-content') ||
      formEl.closest('.product-form') ||
      document
    );
  }

  function applyManufactureNotice(variantId, scope) {
    if (!variantId) return;
    var notices = scope.querySelectorAll(
      '#manufacture-notice, .manufacture-notice'
    );
    if (!notices.length) return;

    var map = window.variantMetafields || {};
    var data = map[String(variantId)];
    var isCustom = !!(data && String(data.customized) === '1');

    notices.forEach(function (n) {
      n.style.display = isCustom ? '' : 'none';
    });
  }

  document.addEventListener('variant:changed', function (event) {
    var v = event && event.detail && event.detail.variant;
    if (!v) return;
    applyManufactureNotice(v.id, scopeFor(event.target));
  });
})();




/* ============================================================
 * Quick-Buy Variant Cascade  v1  (17.05.2026)
 * Theme-Kopie: "color cascading JS /hoffmann"
 *
 * Problem B: Wenn der User auf der Hauptseite eine Farbe wählt,
 * kaskadiert das v7-Cascading die Farbe auf die Empfehlungs-
 * Karten (Cards bekommen die richtige Vorschau und die Card-
 * Links bekommen ?variant=...). Wenn der User dann aber den
 * Quick-Buy-Button einer Card klickt, öffnet sich der Drawer mit
 * der DEFAULT-Variante des Empfehlungs-Produkts — nicht mit der
 * kaskadierten Farbe.
 *
 * Ursache: <quick-buy-drawer> hat ein eigenes href-Attribut,
 * das von Focal beim Öffnen gefetcht wird:
 *   href="/products/handle?view=quick-buy-drawer"
 * Da fehlt das &variant=... Der Server rendert dann mit
 * product.selected_or_first_available_variant.
 *
 * Was dieser Block tut — dreistufig:
 *   1) Proaktiv: Sobald sich der Color-Swatch einer Card ändert
 *      (egal ob durch User-Klick oder durch v7-Cascading), wird
 *      der href der zugehörigen <quick-buy-drawer> /
 *      <quick-buy-popover> direkt mit &variant=<id> versorgt.
 *      Für den NORMALEN Erst-Öffnen-Fall reicht das schon.
 *   2) Reaktiv (capture phase, vor Focals Click-Handler): kurz
 *      bevor Focal den Drawer öffnet, nochmal sicherheitshalber
 *      den href aktualisieren. Falls Schritt 1 das Event
 *      verpasst hat.
 *   3) Post-Load-Korrektur: ein MutationObserver wartet darauf,
 *      dass im Drawer ein <product-variants> erscheint, und ruft
 *      darauf selectVariant(<id>) auf. Das deckt zwei Fälle ab:
 *        a) Focal cached den Drawer-Inhalt und re-fetcht beim
 *           zweiten Öffnen NICHT — der href-Trick allein würde
 *           hier ignoriert. selectVariant korrigiert den state.
 *        b) Falls der Server doch nicht auf den Variant-Param
 *           reagieren sollte (Theme-Konfig-Edge-Case),
 *           korrigiert selectVariant auch das.
 *
 * Nebeneffekt (gewünscht): selectVariant feuert 'variant:changed',
 * was wiederum den Problem-A-Fix anstößt — Manufacture-Notice
 * wird also für die kaskadierte Variante korrekt gesetzt.
 *
 * Einbau: ans Ende von assets/custom.js anhängen, NACH dem
 * Manufacture-Notice-Toggle-Block (Reihenfolge ist nicht
 * kritisch, aber so liest's sich logisch).
 * ============================================================ */
(function () {
  'use strict';

  function desiredVariantFromCard(card) {
    if (!card) return null;
    var r = card.querySelector('.color-swatch__radio:checked');
    if (r && r.dataset && r.dataset.variantId) return r.dataset.variantId;
    return null;
  }

  function setUrlVariantParam(href, variantId) {
    if (!href || !variantId) return href;
    try {
      var u = new URL(href, location.origin);
      u.searchParams.set('variant', variantId);
      return u.pathname + u.search + u.hash;
    } catch (e) {
      return href;
    }
  }

  function updateQuickBuyHrefs(card, variantId) {
    if (!card || !variantId) return;
    card
      .querySelectorAll('quick-buy-drawer, quick-buy-popover')
      .forEach(function (qb) {
        var href = qb.getAttribute('href');
        var newHref = setUrlVariantParam(href, variantId);
        if (newHref && newHref !== href) qb.setAttribute('href', newHref);
      });
  }

  function correctLoadedDrawer(drawer, desiredVariantId) {
    if (!drawer || !desiredVariantId) return false;
    var pv = drawer.querySelector('product-variants');
    if (!pv || typeof pv.selectVariant !== 'function') return false;
    try { pv.selectVariant(Number(desiredVariantId) || desiredVariantId); }
    catch (e) { return false; }
    return true;
  }

  // 1) Proaktiv: Color-Swatch-Change in einer Card → href
  //    der Card-eigenen Quick-Buy-Elements aktualisieren.
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || !t.classList || !t.classList.contains('color-swatch__radio')) return;
    var card = t.closest && t.closest('product-item');
    if (!card) return;
    var vid = desiredVariantFromCard(card);
    if (vid) updateQuickBuyHrefs(card, vid);
  });

  // 2 + 3) Reaktiv beim Klick auf einen Quick-Buy-Trigger.
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest(
      'button[is="toggle-button"]'
    );
    if (!btn) return;
    var ctrl = btn.getAttribute('aria-controls');
    if (!ctrl) return;
    var target = document.getElementById(ctrl);
    if (!target || !target.matches) return;
    if (!target.matches('quick-buy-drawer, quick-buy-popover')) return;

    var card = btn.closest('product-item');
    if (!card) return;
    var vid = desiredVariantFromCard(card);
    if (!vid) return;

    // Schritt 2 — href noch mal nachziehen (falls Schritt 1 was verpasst hat).
    var href = target.getAttribute('href');
    var newHref = setUrlVariantParam(href, vid);
    if (newHref !== href) target.setAttribute('href', newHref);

    // Schritt 3a — falls schon Inhalt da ist (zweites Öffnen, gecacht),
    // sofort korrigieren.
    correctLoadedDrawer(target, vid);

    // Schritt 3b — auf späteren Content warten (erstes Öffnen, fetch läuft).
    var obs = new MutationObserver(function () {
      if (correctLoadedDrawer(target, vid)) obs.disconnect();
    });
    obs.observe(target, { childList: true, subtree: true });
    setTimeout(function () { obs.disconnect(); }, 4000);
  }, true);
})();



/* ============================================================
 * Manufacture-Notice Main-Page Leak-Guard  v1  (17.05.2026)
 * Theme-Kopie: "Kopie von color cascading JS /hoffmann"
 *
 * Warum dieser Patch?
 *
 * Der Manufacture-Notice-Toggle aus Block "Custom-Manufacture-
 * Notice Toggle v1" ist sauber scoped (er rührt nur die Notice
 * im jeweiligen Drawer/Popover bzw. in der jeweiligen
 * .product-form an).
 *
 * Aber: in theme.js (300 KB) ist offenbar noch ein ALTES
 * Listener auf 'variant:changed' aktiv, der ohne Scope arbeitet
 * — vermutlich per document.getElementById('manufacture-notice').
 * Das findet immer NUR die ERSTE Notice im DOM = die der
 * Hauptseite. Wenn man im Quick-Buy-Drawer eine Variante
 * auswählt, die customized=1 hat, togglet dieses Alt-Listener
 * versehentlich die Hauptseiten-Notice.
 *
 * Der Bug existierte technisch schon vorher — war aber
 * unsichtbar, weil der Drawer-Wechsel das window.variantMetafields
 * damals komplett überschrieb (siehe Snippet-Patch 02). Jetzt
 * wo der Drawer korrekt arbeitet und beide Datensätze mergen,
 * trifft das Alt-Listener auf gültige Daten und das Leck wird
 * sichtbar.
 *
 * Was dieser Block tut:
 *  - Lauscht auf 'variant:changed'.
 *  - Wenn das Event aus einem Drawer/Popover kommt, macht er
 *    EINEN Snapshot der display-Styles aller Hauptseiten-
 *    #manufacture-notice-Elemente (= alle, die NICHT in einem
 *    Drawer/Popover sitzen).
 *  - Stellt den Snapshot direkt danach wieder her, zweimal:
 *    einmal sofort (setTimeout 0) und einmal nach ~100 ms — als
 *    Belt-and-Suspenders gegen Listener, die ggf. via
 *    requestAnimationFrame oder mit kleiner Verzögerung
 *    laufen.
 *
 * Effekt: egal was das Alt-Listener auf der Hauptseite anstellt,
 * unser Restore überschreibt es zurück.
 *
 * Einbau: ans Ende von assets/custom.js anhängen, NACH dem
 * Quick-Buy-Variant-Cascade-Block.
 * ============================================================ */
(function () {
  'use strict';

  var DRAWER_SEL =
    'quick-buy-drawer, quick-buy-popover, drawer-content, popover-content';

  function isInDrawer(el) {
    return !!(el && el.closest && el.closest(DRAWER_SEL));
  }

  function snapshotMainNotices() {
    var snaps = [];
    document
      .querySelectorAll('#manufacture-notice, .manufacture-notice')
      .forEach(function (n) {
        if (!isInDrawer(n)) {
          snaps.push({ el: n, display: n.style.display });
        }
      });
    return snaps;
  }

  function restoreSnapshot(snaps) {
    snaps.forEach(function (item) {
      if (item.el.style.display !== item.display) {
        item.el.style.display = item.display;
      }
    });
  }

  document.addEventListener('variant:changed', function (event) {
    // Nur reagieren, wenn das Event aus einem Drawer/Popover stammt.
    if (!isInDrawer(event && event.target)) return;

    var snap = snapshotMainNotices();
    if (!snap.length) return;

    // 1. Restore direkt nach der aktuellen Microtask-Phase
    setTimeout(function () { restoreSnapshot(snap); }, 0);
    // 2. Zweiter Restore als Sicherheitsnetz, falls jemand mit
    //    rAF / kleiner Verzögerung arbeitet
    setTimeout(function () { restoreSnapshot(snap); }, 120);
  });
})();